<template>
  <content-viewer
    v-model:show="isCommentsViewerShown"
    :with-trigger-btn="false"
    :title="$t('comment.title')"
    :is-footer-shown="false"
    :is-full-height="true"
    viewer-class="all-reviews-viewer"
    main-content-class="p-0 pb-4 bg-gray-20"
    @after-enter="onViewerAfterEnter"
    @after-leave="onViewerAfterLeave"
  >
    <etg-infinite-scroll
      class="px-5 bg-gray-10"
      :is-loading="isProductCommentsLoading"
      :items-count="comments.length"
      :total-count="commentsData?.total_count || 0"
      @load-more="onLoadMore"
    >
      <div
        class="py-5 flex flex-col"
        :class="[
          props.funnowAiSummary ? '-mx-5 px-5 bg-[linear-gradient(182.77deg,#FEF6F7_2.31%,#FFFCFA_39.45%,#FFFFFF_97.69%)] md:pb-3' : 'gap-3 md:pt-1',
        ]"
      >
        <p>{{ props.branchName }}</p>

        <template v-if="props.funnowAiSummary">
          <product-ai-summary-header
            class="mt-3"
            :subtitle="$t('ai.summary.eatigo')"
            :tooltip-text="$t('ai.eatigo.notice')"
            title-layout="vertical"
          />
          <p class="mt-2">{{ props.funnowAiSummary.description }}</p>
        </template>

        <div :class="props.funnowAiSummary ? 'mt-5' : ''" class="rating-section flex items-center gap-1">
          <p class="text-2xl font-700">{{ commentsData?.is_new ? $t('comment.new') : commentsData?.avg_rating }}</p>
          <svg-icon color="black-50" name="star-filled" class="size-4" />
          <p v-if="!commentsData?.is_new && commentsData?.total_count">
            {{ $t('comment.review.count', { count: formatNumberWithCommas(commentsData.total_count) }, commentsData.total_count) }}
          </p>
        </div>
        <div v-if="!commentsData?.is_new" class="rating-bars-section flex flex-col-reverse gap-2">
          <div v-for="n in 5" :key="n" class="flex items-center gap-1">
            <p class="text-xs">{{ n }}</p>
            <svg-icon color="black-50" name="star-filled" class="size-3" />
            <div class="flex-1">
              <div class="w-70% h-2 rounded-14px bg-yellow-50" :style="{ width: `${((commentsData?.score?.[n - 1] || 0) / ratingTotal) * 100}%` }" />
            </div>
          </div>
        </div>
      </div>
      <review-tags-section :selected-review-tag-id="selectedReviewTagId" :pid="pid" class="bg-gray-20 gap-2.5" @tag-click="onReviewTagClick" />
      <div class="sort-section py-2 flex justify-end">
        <etg-select
          v-model:value="sortby"
          size="mini"
          class="sorting-select -mr-3"
          type="text"
          :options="sortingOptions"
          :consistent-menu-width="false"
          :menu-props="{ class: 'w-60' }"
          placement="bottom-end"
        />
      </div>
      <div v-for="comment in comments" :key="comment.id" class="-mx-5 px-5 py-4 border-b border-gray-40">
        <review-card
          :review-username="comment.rated_by || ''"
          :review-rating="comment.rating || 1"
          :review-content="comment.description || ''"
          :review-tags="comment.tags || []"
          :review-create-time="comment.comment_time || ''"
          :comment-id="comment.id"
          :is-useful-btn-shown="true"
          :useful-count="comment.like_count"
          :is-useful="comment.is_like"
          :review-source="comment.source"
          placement="viewer"
        />
      </div>
    </etg-infinite-scroll>
  </content-viewer>
</template>

<script setup lang="ts">
import type { GetProductCommentsParams, ProductCommentsSortby } from '@/types/product'
import type { EatigoReviewReviewData, BranchReviewSummaryItem } from '@/types/api'

import ContentViewer from '@/components/common/content-viewer.vue'
import ProductAiSummaryHeader from '@/components/product/product-detail/product-ai-summary-header.vue'
import ReviewTagsSection from '@/components/review/review-tags-section.vue'
import ReviewCard from '@/components/review/review-card.vue'
import EtgInfiniteScroll from '@/components/common/etg-infinite-scroll.vue'

import { REVIEW_CARD_ID_PREFIX, DEFAULT_REVIEW_TAG_ID } from '@/constants/commons'

interface Props {
  pid: string | number
  branchName: string
  funnowAiSummary?: BranchReviewSummaryItem
}

const PAGE_SIZE = 10
const DEFAULT_SORTBY = 'default'

const props = defineProps<Props>()
const { pid } = toRefs(props)

const isCommentsViewerShown = defineModel<boolean>()
const selectedReviewCardId = defineModel<number>('selectedReviewCardId')
const selectedReviewTagId = defineModel<number>('selectedReviewTagId', { default: DEFAULT_REVIEW_TAG_ID })

const { t } = useI18n()

const { pagingState } = usePagination({ pageSize: PAGE_SIZE, mode: 'infinite-scroll', shouldSavePageInURL: false })

const sortby = ref<ProductCommentsSortby>(DEFAULT_SORTBY)
const comments = ref<EatigoReviewReviewData[]>([])

const productCommentsQueryParams = computed<GetProductCommentsParams>(() => ({
  start: (pagingState.page - 1) * pagingState.size,
  size: pagingState.size,
  sortby: sortby.value,
  ...(selectedReviewTagId.value !== DEFAULT_REVIEW_TAG_ID && { tag_id: selectedReviewTagId.value }),
}))

const { data: commentsData, isProductCommentsLoading } = useGetProductComments('all-reviews-viewer', pid, productCommentsQueryParams, {
  debounce: true,
  asyncDataOpts: { lazy: true, server: false },
})

const ratingTotal = computed(() => commentsData.value?.score?.reduce((acc, cur) => acc + cur) || 0)
const sortingOptions = computed(() => [
  { value: 'default', label: t('comment.sorting.default') },
  { value: 'rating_high', label: t('comment.sorting.high_to_low') },
  { value: 'rating_low', label: t('comment.sorting.low_to_high') },
  { value: 'latest', label: t('comment.sorting.recent') },
])

watch(commentsData, newCommentsData => {
  comments.value = comments.value.concat(newCommentsData?.comments || [])
})

watch([selectedReviewTagId, sortby], () => {
  Object.assign(pagingState, getDefaultPagingState())
  comments.value = []
})

function getDefaultPagingState() {
  return { page: 1, size: PAGE_SIZE }
}

function onReviewTagClick(tagId: number) {
  selectedReviewTagId.value = tagId
}

function onLoadMore() {
  pagingState.page += 1
}

function onViewerAfterEnter() {
  if (selectedReviewCardId.value) {
    const theId = ['viewer', REVIEW_CARD_ID_PREFIX, selectedReviewCardId.value].join('-')

    scrollToId(theId, true, { block: 'center' })
  }
}

function onViewerAfterLeave() {
  selectedReviewCardId.value = undefined
  selectedReviewTagId.value = DEFAULT_REVIEW_TAG_ID
  sortby.value = DEFAULT_SORTBY
}
</script>
